import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Anchor, Ship, Clock, CheckCircle2, XCircle, Loader2, Info, Tag } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL, tokenStore, api } from "../lib/api";
import { useAuth } from "../lib/auth";
import Logo from "../components/Logo";

const DAYS_NO = ["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"];

function fmtNok(v) {
  return `${Number(v || 0).toLocaleString("nb-NO", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} kr`;
}
function fmtMoney(v, cur) {
  return `${Number(v || 0).toLocaleString("nb-NO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur === "NOK" ? "kr" : (cur === "EUR" ? "€" : cur)}`;
}

/**
 * Public landing + booking flow for the boat-lift service.
 * URL: /loft (auto-linked from the login screen)
 */
export default function BoatLiftPublic() {
  const [config, setConfig] = useState(null);
  const [quote, setQuote] = useState(null);
  const [step, setStep] = useState("info"); // info | form | account | done
  const [busy, setBusy] = useState(false);
  const { user, refresh } = useAuth();
  const [sp] = useSearchParams();
  const navigate = useNavigate();

  // Booking form data
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    boat_name: "",
    boat_weight_tons: "",
    boat_length_m: "",
    requested_start_at: "",
    requested_end_at: "",
    reason_for_lift: "",
    coupon_code: "",
  });
  // Account step
  const [accountMode, setAccountMode] = useState("guest"); // guest | signup | login
  const [accountForm, setAccountForm] = useState({
    email: "",
    password: "",
    name: "",
    phone: "",
    gdpr_accepted: false,
  });

  // Load config on mount
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/lift/public/config`)
      .then((r) => setConfig(r.data))
      .catch(() => setConfig(null));
  }, []);

  // Prefill from logged-in user
  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        customer_name: f.customer_name || user.name || "",
        customer_email: f.customer_email || user.email || "",
        customer_phone: f.customer_phone || user.phone || "",
      }));
    }
  }, [user]);

  // Handle Stripe return
  useEffect(() => {
    const sid = sp.get("session_id");
    const bid = sp.get("lift_booking");
    if (sid && bid) {
      axios.post(`${BACKEND_URL}/api/lift/stripe/confirm`, { session_id: sid, booking_id: bid })
        .then(() => {
          toast.success("Betaling bekreftet – vi ses ved kaia!");
          setStep("done");
        })
        .catch((e) => {
          toast.error(e?.response?.data?.detail || "Kunne ikke bekrefte betaling");
        });
    }
  }, [sp]);

  // Live quote – guarded against out-of-order responses when the user
  // types quickly in the coupon field.
  useEffect(() => {
    if (!config) return;
    let cancelled = false;
    const body = {
      coupon_code: form.coupon_code || "",
      requested_start_at: form.requested_start_at,
      requested_end_at: form.requested_end_at,
    };
    axios.post(`${BACKEND_URL}/api/lift/quote`, body)
      .then((r) => { if (!cancelled) setQuote(r.data); })
      .catch(() => { if (!cancelled) setQuote(null); });
    return () => { cancelled = true; };
  }, [config, form.coupon_code, form.requested_start_at, form.requested_end_at]);

  const openDaysLabel = useMemo(() => {
    if (!config) return "";
    if (config.days_open.length === 7) return "alle dager";
    return config.days_open.map((d) => DAYS_NO[d]).join(", ");
  }, [config]);

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#A57F39]">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const validateForm = () => {
    const errs = [];
    if (!form.customer_name.trim()) errs.push("navn");
    if (!form.customer_email.trim()) errs.push("e-post");
    if (!form.customer_phone.trim()) errs.push("telefon");
    if (!form.boat_name.trim()) errs.push("båtnavn");
    if (!form.boat_weight_tons) errs.push("båt-vekt");
    if (Number(form.boat_weight_tons) > config.max_weight_tons) {
      errs.push(`vekt må være ≤ ${config.max_weight_tons} t`);
    }
    if (!form.requested_start_at || !form.requested_end_at) errs.push("tidspunkt");
    if (form.requested_start_at && form.requested_end_at &&
        new Date(form.requested_end_at) <= new Date(form.requested_start_at)) {
      errs.push("slutt må være etter start");
    }
    return errs;
  };

  const submitBooking = async () => {
    setBusy(true);
    try {
      // First: if user is not logged in and chose signup/login, handle account
      if (!user) {
        if (accountMode === "signup") {
          if (!accountForm.gdpr_accepted) {
            toast.error("Du må godta personvernerklæringen.");
            setBusy(false); return;
          }
          const r = await axios.post(`${BACKEND_URL}/api/auth/register`, {
            email: accountForm.email || form.customer_email,
            name: accountForm.name || form.customer_name,
            phone: accountForm.phone || form.customer_phone,
            password: accountForm.password,
            gdpr_accepted: true,
          }, { withCredentials: true });
          if (r.data?.access_token) tokenStore.set(r.data.access_token);
          await refresh();
        } else if (accountMode === "login") {
          const r = await axios.post(`${BACKEND_URL}/api/auth/login`, {
            email: accountForm.email,
            password: accountForm.password,
          }, { withCredentials: true });
          if (r.data?.access_token) tokenStore.set(r.data.access_token);
          await refresh();
        }
      }
      // Create the booking
      const r = await axios.post(`${BACKEND_URL}/api/lift/bookings`, {
        ...form,
        boat_weight_tons: Number(form.boat_weight_tons),
        boat_length_m: form.boat_length_m ? Number(form.boat_length_m) : null,
        lang: "no",
      }, { withCredentials: true, headers: tokenStore.get() ? { Authorization: `Bearer ${tokenStore.get()}` } : {} });

      const booking = r.data;
      if (booking.status === "pending_review") {
        toast.success("Sendt inn – vi tar kontakt for å bekrefte tidspunktet.");
        setStep("done");
        return;
      }
      // Start Stripe checkout
      const successUrl = `${window.location.origin}/loft?session_id={CHECKOUT_SESSION_ID}&lift_booking=${booking.id}`;
      const cancelUrl = `${window.location.origin}/loft?cancelled=1`;
      const chk = await axios.post(`${BACKEND_URL}/api/lift/stripe/checkout`, {
        booking_id: booking.id,
        success_url: successUrl,
        cancel_url: cancelUrl,
      });
      window.location.href = chk.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke fullføre bookingen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="lift-public-page">
      <header className="bg-[#0C1824] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 text-white">
            <Logo dark size={32} />
          </Link>
          <div className="flex items-center gap-2">
            {user ? (
              <Link to="/portal">
                <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10">
                  Min side
                </Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10">
                  Logg inn
                </Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-5 sm:space-y-6">
        {/* Hero */}
        <Card className="p-5 sm:p-8 bg-gradient-to-br from-[#0C1824] to-[#11283A] text-white overflow-hidden">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-[#ECC974]">
            <Anchor className="w-3.5 h-3.5" /> Ny tjeneste
          </div>
          <h1 className="text-2xl sm:text-4xl font-bold mt-2">Løft av privat båt</h1>
          <p className="text-sm sm:text-base text-white/85 mt-3 max-w-xl">
            {config.public_notes_no}
          </p>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-white/5 border border-white/10 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#ECC974]">Fast pris</div>
              <div className="font-mono font-bold text-xl mt-1">{fmtNok(config.price_gross)}</div>
              <div className="text-[10px] text-white/60 mt-0.5">
                inkluderer {config.included_hours || 1} time oppe
              </div>
              <div className="text-[10px] text-white/50 mt-0.5">
                deretter {fmtNok(config.extra_hour_price || 0)} per påbegynte time
              </div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#ECC974]">Åpningstid</div>
              <div className="font-semibold mt-1 text-sm">{config.open_from} – {config.open_to}</div>
              <div className="text-[10px] text-white/50 mt-0.5">{openDaysLabel}</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#ECC974]">Maks vekt</div>
              <div className="font-semibold mt-1 text-sm">{config.max_weight_tons} tonn</div>
              <div className="text-[10px] text-white/50 mt-0.5">operatør styrer kranen</div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {config.enabled ? (
              <Button
                onClick={() => setStep("form")}
                data-testid="lift-start-booking"
                className="bg-[#BD984D] hover:bg-[#A57F39] text-[#0C1824] font-semibold h-12 px-6"
              >
                Book løft nå
              </Button>
            ) : (
              <div className="bg-red-500/20 border border-red-400/40 rounded-lg px-4 py-3 text-red-100 text-sm flex items-center gap-2">
                <XCircle className="w-4 h-4" />
                {config.closed_notice_no}
              </div>
            )}
          </div>
        </Card>

        {/* Booking flow */}
        {step === "form" && config.enabled && (
          <Card className="p-5 sm:p-6" data-testid="lift-form">
            <h2 className="text-lg sm:text-xl font-bold text-[#0C1824]">Fyll ut booking</h2>
            <p className="text-xs text-[#2B3F4D] mt-1">
              Etter innsending går du til Stripe for å betale. Ligger tidspunktet
              utenfor åpningstid, blir bookingen sendt til oss for godkjenning først.
            </p>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Navn *</Label>
                <Input data-testid="lift-name" value={form.customer_name}
                       onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
              </div>
              <div>
                <Label>Telefon *</Label>
                <Input data-testid="lift-phone" value={form.customer_phone}
                       onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <Label>E-post *</Label>
                <Input data-testid="lift-email" type="email" value={form.customer_email}
                       onChange={(e) => setForm({ ...form, customer_email: e.target.value })} />
              </div>
              <div>
                <Label>Båtnavn *</Label>
                <Input data-testid="lift-boat-name" value={form.boat_name}
                       onChange={(e) => setForm({ ...form, boat_name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Vekt (t) *</Label>
                  <Input data-testid="lift-weight" type="number" step="0.1" value={form.boat_weight_tons}
                         onChange={(e) => setForm({ ...form, boat_weight_tons: e.target.value })} />
                </div>
                <div>
                  <Label>Lengde (m)</Label>
                  <Input data-testid="lift-length" type="number" step="0.1" value={form.boat_length_m}
                         onChange={(e) => setForm({ ...form, boat_length_m: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Start *</Label>
                <Input data-testid="lift-start" type="datetime-local" value={form.requested_start_at}
                       onChange={(e) => setForm({ ...form, requested_start_at: e.target.value })} />
              </div>
              <div>
                <Label>Slutt *</Label>
                <Input data-testid="lift-end" type="datetime-local" value={form.requested_end_at}
                       onChange={(e) => setForm({ ...form, requested_end_at: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <Label>Hvorfor ønsker du løftet? (valgfritt)</Label>
                <Textarea data-testid="lift-reason" rows={2} value={form.reason_for_lift}
                          onChange={(e) => setForm({ ...form, reason_for_lift: e.target.value })}
                          placeholder="f.eks. bunnstoff, propell-service, vinterlagring" />
              </div>
              <div className="sm:col-span-2">
                <Label className="flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> Rabattkode (valgfritt)</Label>
                <Input data-testid="lift-coupon" value={form.coupon_code}
                       onChange={(e) => setForm({ ...form, coupon_code: e.target.value.toUpperCase() })}
                       placeholder="F.eks. SKIENBF" className="uppercase" />
              </div>
            </div>

            {quote && (
              <div className="mt-5 p-3 sm:p-4 rounded-lg bg-[#BD984D]/10 border border-[#BD984D]/30" data-testid="lift-quote">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#A57F39] font-semibold">Å betale</div>
                    <div className="text-2xl sm:text-3xl font-mono font-bold text-[#0C1824] mt-0.5">
                      {fmtMoney(quote.final_amount, quote.currency)}
                    </div>
                    <div className="text-xs text-[#2B3F4D] mt-1 space-y-0.5">
                      <div>
                        Grunnpris {fmtMoney(quote.base_price, quote.currency)}
                        {quote.included_hours ? ` (inkl. ${quote.included_hours} time oppe)` : ""}
                      </div>
                      {quote.extra_hours > 0 && (
                        <div>
                          + {quote.extra_hours} ekstra {quote.extra_hours === 1 ? "time" : "timer"}
                          {" à "}{fmtMoney(quote.extra_hour_price, quote.currency)}
                          {" = "}{fmtMoney(quote.extra_price, quote.currency)}
                        </div>
                      )}
                      {quote.discount_amount > 0 && (
                        <div>
                          − Rabatt {fmtMoney(quote.discount_amount, quote.currency)}
                          {quote.coupon_label && <> ({quote.coupon_label})</>}
                        </div>
                      )}
                    </div>
                  </div>
                  {form.requested_start_at && form.requested_end_at && (
                    quote.within_open_hours ? (
                      <Badge className="bg-emerald-100 text-emerald-800 border-0">
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Innenfor åpningstid
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-800 border-0">
                        <Info className="w-3.5 h-3.5 mr-1" /> Utenom åpningstid – vi må godkjenne
                      </Badge>
                    )
                  )}
                </div>
              </div>
            )}

            <div className="mt-5 flex gap-2 flex-wrap">
              <Button variant="outline" onClick={() => setStep("info")}>Tilbake</Button>
              <Button
                onClick={() => {
                  const errs = validateForm();
                  if (errs.length) { toast.error(`Fyll inn: ${errs.join(", ")}`); return; }
                  setStep("account");
                }}
                className="bg-[#0C1824] hover:bg-[#152238] text-white flex-1"
                data-testid="lift-form-continue"
              >
                Fortsett
              </Button>
            </div>
          </Card>
        )}

        {step === "account" && (
          <Card className="p-5 sm:p-6" data-testid="lift-account-step">
            <h2 className="text-lg sm:text-xl font-bold text-[#0C1824]">Sist steg – konto</h2>
            {user ? (
              <div className="mt-3 flex items-start gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5" />
                <div className="text-sm">
                  <div>Innlogget som <b>{user.email}</b></div>
                  <div className="text-xs text-slate-600 mt-0.5">Bookingen din havner på Min side.</div>
                </div>
              </div>
            ) : (
              <>
                <p className="text-xs text-[#2B3F4D] mt-1">
                  Vil du opprette konto, får du oversikt over bookingene dine og
                  kan booke igjen raskere.
                </p>
                <div className="grid grid-cols-3 gap-2 mt-4">
                  {[
                    { id: "guest", label: "Fortsett uten konto" },
                    { id: "signup", label: "Opprett konto" },
                    { id: "login", label: "Har konto" },
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setAccountMode(opt.id)}
                      data-testid={`lift-mode-${opt.id}`}
                      className={`text-sm p-3 rounded-lg border-2 text-center transition ${
                        accountMode === opt.id
                          ? "border-[#BD984D] bg-[#BD984D]/10 text-[#0C1824] font-semibold"
                          : "border-slate-200 text-slate-600 hover:border-slate-300"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {accountMode === "signup" && (
                  <div className="mt-4 space-y-3">
                    <div>
                      <Label>E-post</Label>
                      <Input type="email" value={accountForm.email || form.customer_email}
                             onChange={(e) => setAccountForm({ ...accountForm, email: e.target.value })} />
                    </div>
                    <div>
                      <Label>Passord (min 6 tegn)</Label>
                      <Input type="password" value={accountForm.password}
                             data-testid="lift-signup-password"
                             onChange={(e) => setAccountForm({ ...accountForm, password: e.target.value })} />
                    </div>
                    <label className="flex items-start gap-2 text-sm text-[#2B3F4D]">
                      <Checkbox checked={accountForm.gdpr_accepted}
                                onCheckedChange={(v) => setAccountForm({ ...accountForm, gdpr_accepted: !!v })}
                                data-testid="lift-signup-gdpr" />
                      <span>
                        Jeg godtar <Link to="/personvern" target="_blank" className="text-[#A57F39] underline">personvernerklæringen</Link>.
                      </span>
                    </label>
                  </div>
                )}
                {accountMode === "login" && (
                  <div className="mt-4 space-y-3">
                    <div>
                      <Label>E-post</Label>
                      <Input type="email" value={accountForm.email}
                             onChange={(e) => setAccountForm({ ...accountForm, email: e.target.value })} />
                    </div>
                    <div>
                      <Label>Passord</Label>
                      <Input type="password" value={accountForm.password}
                             onChange={(e) => setAccountForm({ ...accountForm, password: e.target.value })} />
                    </div>
                  </div>
                )}
              </>
            )}
            <div className="mt-5 flex gap-2 flex-wrap">
              <Button variant="outline" onClick={() => setStep("form")}>Tilbake</Button>
              <Button
                onClick={submitBooking}
                disabled={busy}
                data-testid="lift-submit"
                className="bg-[#BD984D] hover:bg-[#A57F39] text-[#0C1824] font-semibold flex-1 h-12"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
                {quote?.within_open_hours ? "Bekreft & gå til betaling" : "Send inn forespørsel"}
              </Button>
            </div>
          </Card>
        )}

        {step === "done" && (
          <Card className="p-6 sm:p-8 text-center" data-testid="lift-done">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-[#0C1824]">Tusen takk!</h2>
            <p className="text-sm text-[#2B3F4D] mt-2 max-w-md mx-auto">
              Vi har mottatt bookingen din. Du får en bekreftelse på e-post. Ligger
              tidspunktet utenom åpningstid tar vi kontakt for å avklare før betaling.
            </p>
            <div className="mt-5 flex gap-2 justify-center flex-wrap">
              <Button variant="outline" onClick={() => { setStep("info"); navigate("/loft", { replace: true }); }}>
                Ny booking
              </Button>
              {user && (
                <Link to="/portal">
                  <Button className="bg-[#0C1824] hover:bg-[#152238] text-white">
                    Til Min side
                  </Button>
                </Link>
              )}
            </div>
          </Card>
        )}
      </main>

      <footer className="text-center text-xs text-slate-400 py-6">
        © Canalboats Telemark AS · <Link to="/personvern" className="hover:text-[#A57F39]">Personvern</Link>
      </footer>
    </div>
  );
}
